// src/comps/CommunityMembers.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";

export default function CommunityMembers() {
  const [members, setMembers] = useState([]);
  const [error, setError] = useState("");
  const [sortField, setSortField] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMembers = async () => {
      const token = localStorage.getItem("token");
      try {
        const res = await fetch(`${API_URL}/api/users`, {
          headers: { "x-auth-token": token },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.msg || "Failed to fetch");
        setMembers(data);
      } catch (err) {
        setError(err.message || "Failed to fetch");
      }
    };
    fetchMembers();
  }, []);

  // Get nested values like "address.city"
  const getVal = (obj, path) =>
    path.split(".").reduce((o, k) => (o && o[k] !== undefined ? o[k] : ""), obj) ?? "";

  const handleSortChange = (e) => {
    const field = e.target.value;
    setSortField(field);

    if (!field) return; // no sort selected

    setMembers((prev) => {
      const arr = [...prev];
      arr.sort((a, b) => {
        const va = getVal(a, field)?.toString().toLowerCase() || "";
        const vb = getVal(b, field)?.toString().toLowerCase() || "";
        return va.localeCompare(vb);
      });
      return arr;
    });
  };

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="container mx-auto">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate("/community")}
            className="bg-[#800000] text-white font-bold py-2 px-6 rounded-lg hover:bg-[#9b0000] transition"
          >
            ← Back to Community
          </button>
        </div>

        {/* Title & Sort Dropdown */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <h1 className="text-5xl font-extrabold">Community Members</h1>
          <div className="flex items-center">
            <label className="mr-3 font-medium">Sort by:</label>
            <select
              value={sortField}
              onChange={handleSortChange}
              className="border border-gray-300 rounded-full py-2 px-4 focus:outline-none focus:ring-2 focus:ring-[#800000]"
            >
              <option value="">— select —</option>
              <option value="department">Department</option>
              <option value="residentialHall">Residential Hall</option>
              <option value="role">Membership Type</option>
              <option value="address.city">City</option>
              <option value="address.state">State</option>
              <option value="address.country">Country</option>
            </select>
          </div>
        </div>

        {error && <p className="text-red-500 mb-4">{error}</p>}

        {/* Members Table */}
        <div className="overflow-x-auto rounded-lg shadow">
          <table className="w-full text-left">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-3 border-b">Name</th>
                <th className="p-3 border-b">Email</th>
                <th className="p-3 border-b">BUET Department</th>
                <th className="p-3 border-b">Residential Hall</th>
                <th className="p-3 border-b">Membership Type</th>
                <th className="p-3 border-b">City</th>
                <th className="p-3 border-b">State</th>
                <th className="p-3 border-b">Country</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m._id} className="hover:bg-gray-50">
                  <td className="p-3">{m.firstName} {m.lastName}</td>
                  <td className="p-3">{m.email}</td>
                  <td className="p-3">{m.department || "—"}</td>
                  <td className="p-3">{m.residentialHall || "—"}</td>
                  <td className="p-3">
                    {m.role === 'general' ? 'General' :
                     m.role === 'registered' ? 'Registered' :
                     m.role === 'lifetime' ? 'Lifetime' :
                     m.role || "—"}
                  </td>
                  <td className="p-3">{m.address?.city || "—"}</td>
                  <td className="p-3">{m.address?.state || "—"}</td>
                  <td className="p-3">{m.address?.country || "—"}</td>
                </tr>
              ))}
              {members.length === 0 && !error && (
                <tr>
                  <td className="p-3 text-gray-500" colSpan={8}>
                    No members to display.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
